<script setup>
import { computed, ref } from 'vue';
import TicketPriorityBadge from '../../shared/TicketPriorityBadge.vue';

const props = defineProps({
    tickets: { type: Array, default: () => [] },
    activeTicketId: { type: [String, Number], default: null },
    labels: { type: Object, default: () => ({}) },
    channelName: { type: String, default: '' },
});

const emit = defineEmits(['select-ticket']);
const search = ref('');

const ticketSearchHaystack = (ticket) => [
    ticket.id,
    ticket.id != null ? `#${ticket.id}` : '',
    ticket.title,
    ticket.subject,
    ticket.customer_email,
    ticket.customer_name,
    ticket.customer,
    ticket.status_label,
    ticket.status,
    ticket.priority_label,
    ticket.priority,
]
    .filter((value) => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLocaleLowerCase();

const filteredTickets = computed(() => {
    const needle = search.value.trim().toLocaleLowerCase();
    if (!needle) {
        return props.tickets;
    }
    return props.tickets.filter((ticket) => ticketSearchHaystack(ticket).includes(needle));
});

const isActive = (ticket) => String(ticket.id) === String(props.activeTicketId);
</script>

<template>
    <aside id="ticket-desk-ticket-list" class="ticket-desk-list" data-testid="ticket-desk-ticket-list">
        <div v-if="channelName" class="ticket-desk-list__channel" data-testid="ticket-desk-list-channel">
            {{ channelName }}
        </div>
        <div class="ticket-desk-list__search">
            <span class="ticket-desk-icon ticket-desk-list__search-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
            </span>
            <input
                v-model="search"
                type="search"
                :placeholder="labels.search || 'Cerca ticket'"
                :aria-label="labels.search || 'Cerca ticket'"
            />
        </div>
        <div class="ticket-desk-list__heading">
            <h2>{{ labels.myTickets || labels.my_tickets || 'I miei ticket' }}</h2>
            <span class="ticket-desk-badge ticket-desk-badge--neutral">{{ tickets.length }}</span>
        </div>
        <div v-if="filteredTickets.length" class="ticket-desk-list__rows">
            <button
                v-for="ticket in filteredTickets"
                :key="ticket.id"
                type="button"
                class="ticket-desk-list__row"
                :class="{ 'ticket-desk-list__row--active': isActive(ticket) }"
                @click="emit('select-ticket', ticket)"
            >
                <span class="ticket-desk-list__meta">
                    <strong>#{{ ticket.id }}</strong>
                    <TicketPriorityBadge
                        :priority="ticket.priority"
                        :priority-label="ticket.priority_label"
                        size="sm"
                        test-id="ticket-desk-list-priority"
                    />
                </span>
                <span class="ticket-desk-list__title">{{ ticket.title }}</span>
                <span class="ticket-desk-list__subtitle">
                    {{ ticket.customer_name || ticket.customer_email || ticket.customer }}
                    <template v-if="ticket.status_label || ticket.status"> · {{ ticket.status_label || ticket.status }}</template>
                    <template v-if="ticket.time || ticket.updated_at_label"> · {{ ticket.time || ticket.updated_at_label }}</template>
                </span>
            </button>
        </div>
        <p v-else class="ticket-desk-empty">{{ labels.noTickets || 'Nessun ticket trovato' }}</p>
    </aside>
</template>
